import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Download, Upload } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import {
  Champ,
  ChampPuces,
  ChampSelection,
  ChampTexte,
  MentionObligatoire,
} from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import { STATUTS_FICHE, tonStatutFiche } from "@/composants/fiches";
import { BlocSignatures, type SignatureSaisie } from "@/composants/signature-fiche";
import { messageDe, useChargement } from "@/lib/chargement";
import { dateCourte, horodatage, octets } from "@/lib/libelles";
import {
  api,
  urlContenuDocument,
  urlPdfFiche,
  type ChampModele,
  type Fiche,
  type MediaFiche,
  type ValeurFiche,
} from "@/lib/api";

/**
 * Une fiche (ADR-0060, ticket #107) : remplie d'après une version d'un modèle, sur un
 * lot. En brouillon, elle s'enregistre autant de fois qu'il faut et s'abandonne ; elle
 * se valide quand son type le demande. Validée, elle est figée et se lit seulement.
 *
 * Le serveur contrôle chaque valeur et la complétude, et dit chaque faute en toutes
 * lettres : l'écran n'en redouble aucune. Le formulaire ne bloque donc pas la saisie
 * d'un champ obligatoire vide (`noValidate`) — un brouillon s'enregistre incomplet.
 *
 * Une zone de médias (ticket #110) reçoit les photos et les vidéos prises au téléphone :
 * chaque fichier part seul dès qu'il est choisi, comme un document. Il n'attend pas
 * l'enregistrement du brouillon, et la fiche figée garde les siens.
 *
 * Une fiche dont le type demande des signatures (ticket #112) les montre à son emplacement
 * de signature, ou après ses sections si le lecteur ne le voit pas, et se signe à l'écran.
 */

// Ce que l'écran tient d'un champ : le texte d'une saisie, oui ou non, des options.
type Saisie = string | boolean | string[];

function saisieDe(champ: ChampModele, valeur: ValeurFiche | undefined): Saisie {
  if (valeur === undefined) {
    if (champ.type === "CHOIX_MULTIPLE") return [];
    return "";
  }
  if (typeof valeur === "number") return String(valeur);
  return valeur;
}

/** Ce qui part au serveur. Un nombre mal saisi part tel quel : le serveur dit la faute. */
function valeurDe(champ: ChampModele, saisie: Saisie): unknown {
  if ((champ.type === "NOMBRE" || champ.type === "COTE_MM") && typeof saisie === "string") {
    const texte = saisie.trim().replace(",", ".");
    if (texte === "") return null;
    const nombre = Number(texte);
    return Number.isFinite(nombre) ? nombre : saisie;
  }
  return saisie === "" ? null : saisie;
}

function libelleDe(champ: ChampModele) {
  if (champ.type === "NOMBRE") return `${champ.libelle}, en ${champ.parametres.unite ?? ""}`;
  if (champ.type === "COTE_MM") return `${champ.libelle}, en mm`;
  return champ.libelle;
}

function lecture(champ: ChampModele, valeur: ValeurFiche | undefined): string {
  if (valeur === undefined || valeur === "") return "—";
  if (champ.type === "OUI_NON") return valeur ? "Oui" : "Non";
  if (champ.type === "DATE") return dateCourte(String(valeur));
  if (champ.type === "NOMBRE") return `${String(valeur).replace(".", ",")} ${champ.parametres.unite}`;
  if (champ.type === "COTE_MM") return `${valeur} mm`;
  if (Array.isArray(valeur)) return valeur.length ? valeur.join(", ") : "—";
  return String(valeur);
}

export function PageFiche() {
  const { id = "" } = useParams();
  const charge = useChargement((signal) => api.fiche(id, signal), [id]);
  const [courante, setCourante] = useState<Fiche | null>(null);
  const fiche = courante ?? charge.donnees;
  if (charge.erreur) return <Erreur message={charge.erreur} onReessayer={charge.recharger} />;
  if (!fiche) return <Chargement message="Chargement de la fiche…" />;
  return <ContenuFiche key={`${fiche.id}-${fiche.statut}`} fiche={fiche} onChange={setCourante} />;
}

function ContenuFiche({ fiche, onChange }: { fiche: Fiche; onChange: (fiche: Fiche) => void }) {
  // Un champ lié se lit dans le dossier (#108), une zone de médias se remplit par dépôt
  // (#110), un emplacement de signature en signant (#112) : l'écran n'en saisit ni n'en
  // envoie aucun.
  const champs = fiche.definition.sections
    .flatMap((s) => s.champs)
    .filter(
      (c) =>
        c.type !== "CHAMP_LIE" && c.type !== "ZONE_MEDIAS" && c.type !== "EMPLACEMENT_SIGNATURE",
    );
  const emplacement = fiche.definition.sections
    .flatMap((s) => s.champs)
    .some((c) => c.type === "EMPLACEMENT_SIGNATURE");
  const [saisies, setSaisies] = useState<Record<string, Saisie>>(() =>
    Object.fromEntries(champs.map((c) => [c.cle, saisieDe(c, fiche.valeurs[c.cle])])),
  );
  const [erreur, setErreur] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState(false);
  const [abandon, setAbandon] = useState(false);

  async function agir(travail: () => Promise<Fiche>) {
    setErreur(null);
    setEnregistre(false);
    try {
      const nouvelle = await travail();
      onChange(nouvelle);
      return nouvelle;
    } catch (cause) {
      setErreur(messageDe(cause));
      return null;
    }
  }

  const valeurs = () =>
    Object.fromEntries(champs.map((c) => [c.cle, valeurDe(c, saisies[c.cle])]));
  const enregistrer = () => agir(() => api.enregistrerFiche(fiche.id, valeurs()));
  // Ce qui est à l'écran est ce qui se signe : un brouillon s'enregistre d'abord, quand
  // l'appelant peut le remplir.
  const signer = async (signature: SignatureSaisie) => {
    if (fiche.statut === "BROUILLON" && fiche.modifiable && !(await enregistrer())) return null;
    return agir(() => api.signerFiche(fiche.id, signature));
  };
  const signatures = (titre: string) => (
    <BlocSignatures key="signatures" titre={titre} fiche={fiche} onSigner={signer} />
  );
  const zone = (champ: ChampModele) => (
    <ZoneMedias
      key={champ.cle}
      champ={champ}
      medias={fiche.medias.filter((m) => m.zone === champ.cle)}
      modifiable={fiche.modifiable}
      onDeposer={(fichier) =>
        agir(async () => {
          await api.deposerMediaFiche(fiche.id, champ.cle, fichier);
          return api.fiche(fiche.id);
        })
      }
      onRetirer={(media) =>
        agir(async () => {
          await api.retirerMediaFiche(fiche.id, media.id);
          return api.fiche(fiche.id);
        })
      }
    />
  );

  return (
    <div className="flex flex-col gap-5">
      <Link
        to={`/lots/${fiche.lot_id}`}
        className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Lot {fiche.code_lot}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="break-words text-xl font-semibold text-encre">{fiche.modele_nom}</h1>
          <p className="mt-1 text-sm text-encre-douce">
            {fiche.type_libelle} · version {fiche.version_numero} du modèle
          </p>
        </div>
        <Etiquette ton={tonStatutFiche(fiche.statut)}>{STATUTS_FICHE[fiche.statut]}</Etiquette>
      </div>
      <p className="text-sm text-encre-douce">
        Créée le {horodatage(fiche.cree_le)}
        {fiche.cree_par ? ` par ${fiche.cree_par}` : ""}
        {fiche.figee_le
          ? ` · ${fiche.statut === "VALIDEE" ? "validée" : "signée"} le ${horodatage(fiche.figee_le)}${
              fiche.figee_par ? ` par ${fiche.figee_par}` : ""
            }, elle ne change plus`
          : ""}
        {fiche.abandonnee_le ? ` · abandonnée le ${horodatage(fiche.abandonnee_le)}` : ""}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <a
          href={urlPdfFiche(fiche.id)}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-[6px] border border-filet px-3 text-sm text-encre hover:bg-papier"
        >
          <Download className="h-4 w-4" aria-hidden />
          Télécharger le PDF
        </a>
        <p className="min-w-0 break-all text-sm text-encre-douce">
          {fiche.pdf_scelle
            ? `PDF scellé au dossier · empreinte SHA-256 ${fiche.pdf_scelle.hash_sha256.slice(0, 16)}…`
            : fiche.statut === "SIGNEE" && fiche.signatures_manquantes.length
              ? "PDF non scellé : il le sera à la dernière signature attendue."
              : fiche.figee_le
                ? "PDF non scellé : il ne montre que ce que vous voyez de la fiche."
                : "PDF du brouillon, non scellé."}
        </p>
      </div>

      {erreur ? <Erreur message={erreur} /> : null}

      <form
        noValidate
        className="flex flex-col gap-5"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          void enregistrer().then((nouvelle) => setEnregistre(nouvelle !== null));
        }}
      >
        {fiche.modifiable ? <MentionObligatoire /> : null}
        {fiche.definition.sections.map((section) => (
          <Carte key={section.cle}>
            <EnTeteCarte>
              <h2 className="text-sm font-semibold text-encre">{section.titre}</h2>
            </EnTeteCarte>
            <CorpsCarte>
              {fiche.modifiable ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  {section.champs.map((champ) =>
                    champ.type === "ZONE_MEDIAS" ? (
                      zone(champ)
                    ) : champ.type === "EMPLACEMENT_SIGNATURE" ? (
                      signatures(champ.libelle)
                    ) : champ.type === "CHAMP_LIE" ? (
                      <dl key={champ.cle}>
                        <Donnee libelle={`${champ.libelle}, repris du dossier`}>
                          {fiche.valeurs_liees[champ.cle] ?? "—"}
                        </Donnee>
                      </dl>
                    ) : (
                      <SaisieChamp
                        key={champ.cle}
                        champ={champ}
                        saisie={saisies[champ.cle]}
                        onChange={(saisie) => {
                          setEnregistre(false);
                          setSaisies((avant) => ({ ...avant, [champ.cle]: saisie }));
                        }}
                      />
                    ),
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {section.champs.map((champ) =>
                    champ.type === "ZONE_MEDIAS" ? (
                      zone(champ)
                    ) : champ.type === "EMPLACEMENT_SIGNATURE" ? (
                      signatures(champ.libelle)
                    ) : (
                      <dl key={champ.cle}>
                        <Donnee libelle={champ.libelle}>
                          {champ.type === "CHAMP_LIE"
                            ? (fiche.valeurs_liees[champ.cle] ?? "—")
                            : lecture(champ, fiche.valeurs[champ.cle])}
                        </Donnee>
                      </dl>
                    ),
                  )}
                </div>
              )}
            </CorpsCarte>
          </Carte>
        ))}
        {!emplacement && (fiche.signatures.length || fiche.signatures_attendues.length) ? (
          <Carte>
            <CorpsCarte>{signatures("Signatures")}</CorpsCarte>
          </Carte>
        ) : null}

        {fiche.modifiable || fiche.validable ? (
          <div className="flex flex-wrap items-center gap-2">
            {fiche.modifiable ? <Bouton type="submit">Enregistrer le brouillon</Bouton> : null}
            {fiche.validable ? (
              <Bouton
                type="button"
                variante="principal"
                onClick={async () => {
                  // Ce qui est à l'écran est ce qui se valide : il s'enregistre d'abord,
                  // quand l'appelant peut le remplir ; sinon il valide ce qu'il lit.
                  if (!fiche.modifiable || (await enregistrer()))
                    await agir(() => api.validerFiche(fiche.id));
                }}
              >
                Valider la fiche
              </Bouton>
            ) : null}
            {!fiche.modifiable ? null : abandon ? (
              <>
                <Bouton
                  type="button"
                  variante="discret"
                  onClick={() => void agir(() => api.abandonnerFiche(fiche.id))}
                >
                  Confirmer l'abandon
                </Bouton>
                <Bouton type="button" variante="discret" onClick={() => setAbandon(false)}>
                  Garder la fiche
                </Bouton>
              </>
            ) : (
              <Bouton type="button" variante="discret" onClick={() => setAbandon(true)}>
                Abandonner la fiche
              </Bouton>
            )}
            {enregistre ? (
              <span className="inline-flex items-center gap-1 text-sm text-encre-douce">
                <Check className="h-4 w-4" aria-hidden />
                Brouillon enregistré
              </span>
            ) : null}
          </div>
        ) : null}
      </form>
    </div>
  );
}

// Les formats qu'une zone reçoit ; la base refuse tout autre fichier (contrôle 125).
const FORMATS_MEDIA = "image/jpeg,image/png,image/heic,image/webp,video/mp4,video/quicktime";

function ZoneMedias({
  champ,
  medias,
  modifiable,
  onDeposer,
  onRetirer,
}: {
  champ: ChampModele;
  medias: MediaFiche[];
  modifiable: boolean;
  onDeposer: (fichier: File) => Promise<unknown>;
  onRetirer: (media: MediaFiche) => Promise<unknown>;
}) {
  const [envoi, setEnvoi] = useState<string | null>(null);
  const champId = `zone-${champ.cle}`;

  async function deposer(fichiers: FileList | null) {
    // Un fichier après l'autre : une vidéo de 29 Mo ne part pas en même temps que
    // dix photos, et chaque refus se lit pour le fichier qui l'a reçu.
    for (const fichier of Array.from(fichiers ?? [])) {
      setEnvoi(`Envoi de ${fichier.name}…`);
      if ((await onDeposer(fichier)) === null) break;
    }
    setEnvoi(null);
  }

  return (
    <section className="flex flex-col gap-2 sm:col-span-2" aria-labelledby={`${champId}-titre`}>
      <h3 id={`${champId}-titre`} className="text-sm font-medium text-encre">
        {champ.libelle}
        {modifiable && champ.obligatoire ? (
          <span aria-hidden className="ml-0.5 text-alerte">
            *
          </span>
        ) : null}
        <span className="font-normal text-encre-douce">
          {" "}
          · {medias.length === 0 ? "aucun média" : `${medias.length} média${medias.length > 1 ? "s" : ""}`}
        </span>
      </h3>
      {medias.length ? (
        <ul className="flex flex-col divide-y divide-filet rounded-[6px] border border-filet">
          {medias.map((media) => (
            <li key={media.id} className="flex flex-wrap items-center justify-between gap-2 p-2">
              <div className="min-w-0">
                <p className="break-all text-sm text-encre">{media.nom_fichier_origine}</p>
                <p className="text-xs text-encre-douce">
                  {media.type_mime.startsWith("video/") ? "Vidéo" : "Photo"} ·{" "}
                  {octets(media.taille_octets)} · déposée le {horodatage(media.cree_le)}
                  {media.depose_par ? ` par ${media.depose_par}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={urlContenuDocument(media.id)}
                  className="inline-flex min-h-11 items-center gap-1.5 rounded-[6px] border border-filet px-3 text-sm text-encre hover:bg-papier"
                >
                  <Download className="h-4 w-4" aria-hidden />
                  Télécharger
                </a>
                {modifiable ? (
                  <Bouton type="button" variante="discret" onClick={() => void onRetirer(media)}>
                    Retirer
                  </Bouton>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      {modifiable ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={champId} className="inline-flex items-center gap-1.5 text-sm text-encre">
            <Upload className="h-4 w-4 text-encre-douce" aria-hidden />
            Ajouter des photos ou des vidéos
          </label>
          <input
            id={champId}
            type="file"
            multiple
            accept={FORMATS_MEDIA}
            disabled={envoi !== null}
            onChange={(evenement) => {
              const champFichier = evenement.currentTarget;
              void deposer(champFichier.files).then(() => {
                champFichier.value = "";
              });
            }}
            className="min-h-11 text-sm text-encre file:mr-3 file:rounded-[6px] file:border file:border-filet file:bg-white file:px-3 file:py-2 file:text-sm file:text-encre"
          />
          <p className="text-xs text-encre-douce" aria-live="polite">
            {envoi ??
              "Photo jpeg, png, heic ou webp, 25 Mio au plus ; vidéo mp4 ou mov, jusqu'à la taille réglée par l'Administrateur."}
          </p>
        </div>
      ) : null}
    </section>
  );
}

function SaisieChamp({
  champ,
  saisie,
  onChange,
}: {
  champ: ChampModele;
  saisie: Saisie;
  onChange: (saisie: Saisie) => void;
}) {
  const libelle = libelleDe(champ);
  const requis = champ.obligatoire;
  const texte = typeof saisie === "string" ? saisie : "";
  switch (champ.type) {
    case "TEXTE_LONG":
      return (
        <div className="sm:col-span-2">
          <ChampTexte
            libelle={libelle}
            required={requis}
            value={texte}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    case "NOMBRE":
    case "COTE_MM":
      return (
        <Champ
          libelle={libelle}
          required={requis}
          inputMode={champ.type === "COTE_MM" ? "numeric" : "decimal"}
          value={texte}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "DATE":
      return (
        <Champ
          libelle={libelle}
          type="date"
          required={requis}
          value={texte}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "OUI_NON":
      return (
        <ChampPuces
          libelle={libelle}
          required={requis}
          valeur={saisie === true ? "oui" : saisie === false ? "non" : ""}
          onChanger={(choix) => onChange(choix === "oui")}
          options={[
            { valeur: "oui", libelle: "Oui" },
            { valeur: "non", libelle: "Non" },
          ]}
        />
      );
    case "CHOIX_UNIQUE":
      return (
        <ChampSelection
          libelle={libelle}
          required={requis}
          value={texte}
          onChange={(e) => onChange(e.target.value)}
          options={[
            { valeur: "", libelle: "Non renseigné" },
            ...(champ.parametres.options ?? []).map((o) => ({ valeur: o, libelle: o })),
          ]}
        />
      );
    case "CHOIX_MULTIPLE": {
      const cochees = Array.isArray(saisie) ? saisie : [];
      return (
        <fieldset className="flex min-w-0 flex-col gap-1.5 border-0 p-0">
          <legend className="text-sm font-medium text-encre">
            {libelle}
            {requis ? (
              <span aria-hidden className="ml-0.5 text-alerte">
                *
              </span>
            ) : null}
          </legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {(champ.parametres.options ?? []).map((option) => (
              <label key={option} className="inline-flex min-h-11 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={cochees.includes(option)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...cochees, option]
                        : cochees.filter((o) => o !== option),
                    )
                  }
                />
                {option}
              </label>
            ))}
          </div>
        </fieldset>
      );
    }
    default:
      return (
        <Champ
          libelle={libelle}
          required={requis}
          maxLength={500}
          value={texte}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
